import { Target, Users, Zap, Trophy, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/Footer";

export default function About() {
  const team = [
    {
      name: "Marcus Thorne",
      role: "Founder & CEO",
      image: "https://vibe.filesafe.space/1779537497488192178/assets/219efd25-d69f-4663-b4eb-d77b5420c7ec.png",
      bio: "Former agency owner who realized the traditional retainer model was broken. Founded Danu to sell outcomes, not just hours."
    },
    {
      name: "Sarah Jenkins",
      role: "Director of Growth",
      image: "https://vibe.filesafe.space/1779537497488192178/assets/73d5aa05-fc10-4257-a390-aa4d160aa27b.png",
      bio: "Obsessed with conversion rates. Sarah has managed over $10M in ad spend and engineered funnels for top service brands."
    },
    {
      name: "David Chen",
      role: "Lead Systems Architect",
      image: "https://vibe.filesafe.space/1779537497488192178/assets/4aeedebc-b03a-47fb-aa27-9f9d4de2aa5d.png",
      bio: "The genius behind our AI integrations. David ensures your CRM, bots, and pipelines talk to each other flawlessly."
    },
    {
      name: "Elena Rodriguez",
      role: "Head of Client Success",
      image: "https://vibe.filesafe.space/1779537497488192178/assets/bdc4dced-3b35-40c5-acc0-48a1a940799e.png",
      bio: "Your strategic partner. Elena ensures every client hits their revenue targets and maximizes their ROI month over month."
    }
  ];

  const timeline = [
    {
      year: "2019",
      title: "The Vision Born",
      desc: "Founded out of frustration with traditional marketing agencies that delivered vanity metrics instead of actual booked revenue."
    },
    {
      year: "2021",
      title: "The System Perfected",
      desc: "Developed our proprietary lead-to-booking engine, combining high-converting funnels with automated CRM follow-ups."
    },
    {
      year: "2023",
      title: "AI Integration Era",
      desc: "Pioneered the use of conversational AI to qualify leads and book appointments 24/7 without human intervention."
    },
    {
      year: "2024",
      title: "National Expansion",
      desc: "Scaled our operations to serve elite service businesses across 144+ cities nationwide, driving over $50M in client revenue."
    }
  ];

  const values = [
    {
      icon: Target,
      title: "Outcomes Over Outputs",
      desc: "We don't care about vanity metrics. If it doesn't lead to booked appointments and closed deals, we don't do it."
    },
    {
      icon: Zap,
      title: "Speed to Impact",
      desc: "Business moves fast. We deploy fully functional, high-converting systems in 14 days, not 14 weeks."
    },
    {
      icon: Users,
      title: "True Partnership",
      desc: "No long-term contracts holding you hostage. We earn your business every single month by delivering real ROI."
    },
    {
      icon: Trophy,
      title: "Data-Driven Decisions",
      desc: "Gut feelings lose money. We track every click, lead, and dollar to optimize your growth engine relentlessly."
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground font-sans overflow-x-hidden">
      <SEOHead 
        title="About Us"
        description="We are a team of growth engineers, marketers, and developers dedicated to building systems that predictably scale service businesses."
        canonical="/about"
      />
      <Header />
      
      <main className="pt-24">
        {/* HERO */}
        <section className="relative py-20 md:py-32 overflow-hidden border-b border-primary/10">
          <div className="absolute top-[-80px] left-[-80px] w-[360px] h-[360px] bg-accent/10 blur-[80px] pointer-events-none" />
          <div className="max-w-4xl mx-auto px-5 text-center relative z-10">
            <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-4 border border-accent/20 bg-accent/10 px-3 py-1">
              About Danu Marketing
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
              We engineer <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">predictable growth.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              We started Danu Marketing because we were tired of seeing service businesses get burned by marketing agencies that promised the world and delivered nothing but a pretty website. We build the engine, not just the paint job.
            </p>
          </div>
        </section>

        {/* VALUES */}
        <section className="py-20 bg-secondary border-b border-primary/10">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Our Core Values</h2>
              <p className="text-muted-foreground">The principles that drive every system we build.</p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, i) => (
                <div key={i} className="bg-background border border-primary/20 p-8 hover:border-accent hover:-translate-y-2 hover:shadow-xl transition-all duration-300 group">
                  <div className="w-12 h-12 bg-primary/5 text-primary group-hover:bg-accent/10 group-hover:text-accent flex items-center justify-center mb-6 border border-primary/10 group-hover:border-accent/30 transition-colors">
                    <value.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-lg mb-3">{value.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TIMELINE */}
        <section className="py-20 md:py-32 bg-background border-t border-primary/10 relative overflow-hidden">
          <div className="max-w-5xl mx-auto px-5 relative z-10">
            <div className="text-center mb-20">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Timeline</span>
              <h2 className="text-3xl md:text-5xl font-bold mb-4">Our Journey</h2>
              <p className="text-muted-foreground text-lg">How we built the ultimate growth engine.</p>
            </div>
            
            <div className="space-y-12 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-primary/20 before:to-transparent z-10">
              {timeline.map((item, i) => (
                <div key={i} className="relative flex items-center group">
                  {/* Dot */}
                  <div className="absolute left-5 md:left-1/2 w-10 h-10 -translate-x-1/2 flex items-center justify-center rounded-full border-4 border-background bg-primary/10 group-hover:bg-accent group-hover:shadow-[0_0_15px_rgba(184,151,90,0.5)] transition-all duration-300 z-10">
                    <div className="w-3 h-3 bg-primary group-hover:bg-background rounded-full transition-colors" />
                  </div>
                  
                  {/* Content Box */}
                  <div className={`w-[calc(100%-4rem)] ml-auto md:ml-0 md:w-[calc(50%-3rem)] p-8 bg-secondary border border-primary/10 hover:border-accent/50 transition-all duration-300 group-hover:-translate-y-1 ${i % 2 === 0 ? 'md:mr-auto md:text-right' : 'md:ml-auto'}`}>
                    <div className="text-accent font-black text-3xl mb-3 tracking-tighter">{item.year}</div>
                    <h3 className="font-bold text-xl mb-2 text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TEAM */}
        <section className="py-20 md:py-32 bg-background">
          <div className="max-w-6xl mx-auto px-5">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div className="max-w-2xl">
                <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">The Team</span>
                <h2 className="text-3xl md:text-5xl font-bold mb-4">Meet the experts behind <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">your success.</span></h2>
                <p className="text-muted-foreground text-lg">
                  We are a lean, elite team of engineers, marketers, and strategists completely obsessed with scaling service businesses.
                </p>
              </div>
            </div>

            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 4000,
                }),
              ]}
              className="w-full"
            >
              <CarouselContent className="-ml-4 md:-ml-8">
                {team.map((member, i) => (
                  <CarouselItem key={i} className="pl-4 md:pl-8 md:basis-1/2 lg:basis-1/4">
                    <div className="group relative h-full">
                      <div className="aspect-square w-full mb-6 overflow-hidden border border-primary/30 relative bg-primary/5">
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <img 
                          src={member.image} 
                          alt={member.name} 
                          loading="lazy"
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 scale-100 group-hover:scale-105"
                        />
                      </div>
                      <h3 className="font-bold text-xl mb-1">{member.name}</h3>
                      <p className="text-accent text-sm font-bold uppercase tracking-wider mb-3">{member.role}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{member.bio}</p>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex justify-end gap-4 mt-8">
                <CarouselPrevious className="static translate-y-0 w-12 h-12 rounded-none border-primary text-primary hover:bg-primary hover:text-accent" />
                <CarouselNext className="static translate-y-0 w-12 h-12 rounded-none border-primary text-primary hover:bg-primary hover:text-accent" />
              </div>
            </Carousel>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(184,151,90,0.15),transparent_60%)] pointer-events-none" />
          <div className="max-w-3xl mx-auto px-5 text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to scale with us?</h2>
            <p className="text-lg text-primary-foreground/80 mb-10">
              Stop settling for mediocrity. Let's build a growth engine that fills your pipeline with high-quality leads on autopilot.
            </p>
            <Button 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-primary font-bold px-8 py-6 text-lg shadow-lg border border-accent rounded-none"
              onClick={() => window.location.href = '/#contact'}
            >
              Get Your Free Growth Audit <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
